import { collectStoredAnnotations } from '../../annotations';
import type { InstanceRef, SeriesRef, StudyRef } from '../../dicomweb';
import type { ViewerShell } from '../../shell';
import { isImageSeries } from '../../study';
import type { Viewport } from '../../viewport';
import { isSessionStudy } from '../session';
import { activeStudyUID } from '../util';
import type { FindingLocation, MeasurementContext, ViewportContext } from './api';

/**
 * What the Asistente knows about the screen: which study the conversation is about, the image on the
 * active viewport (series, instance, frame, stack position, VOI) and where a finding's slice is.
 */

/** ris `AskIn.measurements` bounds: one value past them and ris refuses the whole question (422). */
const MAX_MEASUREMENTS = 20;
const MAX_LABEL = 60;
const MAX_VALUE = 30;
const MAX_UNIT = 10;
const VALUE_DECIMALS = 100;

function measurementValue(value: number | string): string {
  const text = typeof value === 'number' ? String(Math.round(value * VALUE_DECIMALS) / VALUE_DECIMALS) : String(value);
  return text.slice(0, MAX_VALUE);
}

/** The study of the active cell when the ticket covers it, else the study the viewer was opened on. */
export function conversationStudy(shell: ViewerShell): string {
  const uid = activeStudyUID(shell);
  return isSessionStudy(uid) ? uid : shell.study().studyInstanceUID;
}

/** Priors in the browser: every study but the conversation's (all of them this patient's). */
export function priorStudies(shell: ViewerShell, current: string): StudyRef[] {
  return shell.studies().filter((s) => s.studyInstanceUID !== current);
}

/** Instance and 1-based frame at stack position `index`. */
export function frameAt(series: SeriesRef, index: number): { instance: InstanceRef; frame: number } | undefined {
  let remaining = index;
  for (const instance of series.instances) {
    if (remaining < instance.numberOfFrames) return { instance, frame: remaining + 1 };
    remaining -= instance.numberOfFrames;
  }
  return undefined;
}

/** Stack position of `instanceUid` frame `frame` in `series`, or undefined when it is not there. */
export function indexOf(series: SeriesRef, instanceUid: string, frame: number): number | undefined {
  let before = 0;
  for (const instance of series.instances) {
    if (instance.sopInstanceUID === instanceUid) return frame <= instance.numberOfFrames ? before + frame - 1 : undefined;
    before += instance.numberOfFrames;
  }
  return undefined;
}

/** Image series of any study in the browser with this UID (a derived tile must hold the instance). */
export function findSeries(shell: ViewerShell, seriesUid: string, instanceUid?: string | null): SeriesRef | undefined {
  const all = shell.studies().flatMap((s) => s.series).filter((s) => s.seriesInstanceUID === seriesUid && isImageSeries(s));
  if (instanceUid) return all.find((s) => s.instances.some((i) => i.sopInstanceUID === instanceUid)) ?? all[0];
  return all[0];
}

/** Series + stack position a finding points at, if that series is in the browser. */
export function resolveLocation(shell: ViewerShell, location: FindingLocation): { series: SeriesRef; index: number } | undefined {
  const series = findSeries(shell, location.series_uid, location.instance_uid);
  if (!series) return undefined;
  const byInstance = location.instance_uid ? indexOf(series, location.instance_uid, location.frame) : undefined;
  const count = series.instances.reduce((sum, i) => sum + i.numberOfFrames, 0);
  const index = byInstance ?? location.index ?? undefined;
  if (index === undefined || index < 0 || index >= count) return undefined;
  return { series, index };
}

/** The image on the active viewport, with its window, when it belongs to `studyUid`. */
export function viewportContext(shell: ViewerShell, studyUid: string): ViewportContext | undefined {
  const vp = shell.activeViewport();
  return vp ? viewportContextOf(vp, studyUid) : undefined;
}

export function viewportContextOf(vp: Viewport, studyUid: string): ViewportContext | undefined {
  const stack = vp.currentStack;
  const index = vp.currentIndex();
  if (!stack || index === undefined || !vp.currentImage() || stack.series.studyInstanceUID !== studyUid) return undefined;
  const at = frameAt(stack.series, index);
  if (!at) return undefined;
  const context: ViewportContext = {
    series_uid: stack.series.seriesInstanceUID,
    instance_uid: at.instance.sopInstanceUID,
    frame: at.frame,
    index,
    count: stack.imageIds.length,
  };
  const voi = window.cornerstone.getViewport(vp.element)?.voi;
  if (voi && Number.isFinite(voi.windowWidth) && Number.isFinite(voi.windowCenter) && voi.windowWidth > 0
      && !isDefaultWindow(vp, voi)) {
    context.window_width = Math.round(voi.windowWidth * 100) / 100;
    context.window_center = Math.round(voi.windowCenter * 100) / 100;
  }
  return context;
}

const SAME_WINDOW = 0.01; // within 1 % of the default: the reader has not changed the window

/**
 * The window the image opened with (cornerstone's default for it). Not sent: ris then renders the
 * PACS's own default, which is how the study was set up to be read and what the anonymisation
 * check handles best. A window the reader chose (W/L drag, preset) is always sent.
 */
function isDefaultWindow(vp: Viewport, voi: { windowWidth: number; windowCenter: number }): boolean {
  const image = vp.currentImage();
  if (!image) return false;
  const initial = window.cornerstone.getDefaultViewportForImage(vp.element, image)?.voi;
  if (!initial || !(initial.windowWidth > 0)) return false;
  const close = (a: number, b: number) => Math.abs(a - b) <= SAME_WINDOW * Math.max(Math.abs(b), initial.windowWidth);
  return close(voi.windowWidth, initial.windowWidth) && close(voi.windowCenter, initial.windowCenter);
}

/** Measurement values on the study (label, value, unit), read from the workspace without changing it. */
export function measurementContext(studyUid: string): MeasurementContext[] {
  const out: MeasurementContext[] = [];
  for (const item of collectStoredAnnotations()) {
    if (item.study_uid !== studyUid) continue;
    for (const [value, unit, label] of item.results) {
      if (label === 'text' || (typeof value === 'number' && !Number.isFinite(value))) continue;
      out.push({ label: String(label || item.type).slice(0, MAX_LABEL), value: measurementValue(value), unit: String(unit ?? '').slice(0, MAX_UNIT) });
      if (out.length >= MAX_MEASUREMENTS) return out;
    }
  }
  return out;
}
